import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { every, refresh } from '@signalvote/data';
import type {
  RecurringCutoffSummary,
  RecurringCutoffActionResult,
  RecurringDonorDto,
  RecurringDonorCancelResponse,
  PullInCutoffRequest,
} from '@signalvote/shared';
import { failureFromResponse } from '@signalvote/shared';
import { apiJson, type ApiErrorSpec } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';
import { FINANCE_STALE_TIME } from './queryUtils';

// ADR-069 (#616) — the platform-admin / finance-manager Recurring Cutoff surface. A read summary plus
// two campaign-wide wind-down mutations (pull the cutoff in / stop all recurring now). These are bulk
// sweeps over the whole committee, not single-cached-row edits, so there is no per-row concurrency
// token to thread. Each write's set names RecurringDonation, which refreshes the summary and the donors list.

export type { RecurringCutoffSummary, RecurringCutoffActionResult, RecurringDonorDto };

/** GET /api/finance/recurring — availability + effective cutoff + active-series count. */
const recurringCutoffSummary = defineQuery({
  key: () => ['finance', 'recurring'],
  reads: 'GET /api/finance/recurring',
  staleTime: FINANCE_STALE_TIME,
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<RecurringCutoffSummary>('/api/finance/recurring', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch recurring cutoff summary',
    }),
});

export function useRecurringCutoffSummary() {
  return recurringCutoffSummary(undefined);
}

/**
 * POST /api/finance/recurring/pull-in-cutoff — set an earlier cancel_at on every active subscription
 * bounded later than `cutoff` (an ISO `yyyy-MM-dd` date). A non-future date is refused with
 * `CutoffNotInFuture`.
 */
/** A refusal keeps the server's own message, so a non-future cutoff says why. */
const actionError: ApiErrorSpec = (body, response) =>
  failureFromResponse(response, body, body.error || 'Recurring cutoff action failed');

export const usePullInRecurringCutoff = defineWrite({
  send: (cutoff: string) =>
    apiJson<RecurringCutoffActionResult>('/api/finance/recurring/pull-in-cutoff', {
      method: 'POST',
      json: { cutoff } satisfies PullInCutoffRequest,
      error: actionError,
    }),
});

/** POST /api/finance/recurring/stop-all — cancel every active subscription for the campaign now; each mirror moves to Canceled. */
export const useStopAllRecurring = defineWrite({
  send: (_: undefined) =>
    apiJson<RecurringCutoffActionResult>('/api/finance/recurring/stop-all', {
      method: 'POST',
      error: actionError,
    }),
});

// The per-donor recurring-donors list on the Donations page — who is giving recurringly (donor / amount /
// cadence / started / next-charge / status). A pure DB read; Finance/View. Distinct from the platform-admin
// wind-down summary above.

/** GET /api/finance/recurring/donors — the campaign's recurring donors, Active first. */
const recurringDonors = defineQuery({
  key: () => ['finance', 'recurring', 'donors'],
  reads: 'GET /api/finance/recurring/donors',
  staleTime: FINANCE_STALE_TIME,
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<RecurringDonorDto[]>('/api/finance/recurring/donors', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch recurring donors',
    }),
});

export function useRecurringDonors() {
  return recurringDonors(undefined);
}

/** Fetches the donors list again after it failed: no write happened, so no write set refreshes it. */
export function useRetryRecurringDonors() {
  const queryClient = useQueryClient();
  return useCallback(() => void refresh(queryClient, recurringDonors, every), [queryClient]);
}

/**
 * POST /api/finance/recurring/donors/{id}/cancel — cancel ONE donor's recurring series (Finance/Manage).
 * The status guard on the server makes a racing double-cancel a clean no-op, so there is no per-row
 * concurrency token to thread here.
 */
export const useCancelRecurringDonor = defineWrite({
  send: (recurringDonationId: string) =>
    apiJson<RecurringDonorCancelResponse>(`/api/finance/recurring/donors/${recurringDonationId}/cancel`, {
      method: 'POST',
      error: 'Failed to cancel the recurring donation',
    }),
});
